import { isEmpty } from "@swiggy-private/js-utils";
import type { INormalItemProps } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import type { AppliedFilters, ICartItems } from "@swiggy-private/portal-dineout-context";
import {
    CROSS_SELL_ANALYTICS_SECTION,
    CROSS_SELL_CUSTOMIZABLE_FALSE,
    CROSS_SELL_CUSTOMIZABLE_TRUE,
    CROSS_SELL_MENU_DEFAULT_PARENT_TYPE,
    VEG_FACET_QUERY_ID,
    VEG_PREFERENCE,
} from "./constants";
import type {
    BuildParentCrossSellAnalyticsContextParams,
    CrossSellAnalyticsCustomizationRow,
    CrossSellParentAnalyticsContext,
    CrossSellParentImpressionSupplement,
} from "./types";
import type { CrossSellAnalyticsParams } from "../../types";

export const getAllAppliedFilterValues = (appliedFilters?: AppliedFilters): string[] => [
    ...Object.values(appliedFilters?.singleSelect ?? {}),
    ...Object.values(appliedFilters?.multiSelect ?? {}).flat(),
];

export const buildCrossSellAnalyticsParams = (
    sourceItemId: string,
    preference: string,
    appliedFilters?: AppliedFilters
): CrossSellAnalyticsParams => {
    const appliedFilterValues = getAllAppliedFilterValues(appliedFilters);
    return {
        sourceItemId,
        filterEnabled: !!preference || !isEmpty(appliedFilterValues),
        vegFilterEnabled:
            preference === VEG_PREFERENCE || appliedFilterValues.includes(VEG_FACET_QUERY_ID),
    };
};

const hasCustomizations = (dish?: INormalItemProps, parentItemInCart?: ICartItems): boolean => {
    if (
        parentItemInCart?.addons?.length ||
        parentItemInCart?.variantsV2?.some((v) => v.variantGroups?.length)
    ) {
        return true;
    }
    if (dish?.addons?.length) return true;
    if (dish?.variants?.variantGroups?.length) return true;
    if (dish?.variantsV2?.variantGroups?.length) return true;
    return false;
};

const buildCustomizationsPayload = (
    parentItemInCart?: ICartItems
): CrossSellAnalyticsCustomizationRow[] => {
    if (!parentItemInCart) return [];
    const out: CrossSellAnalyticsCustomizationRow[] = [];
    for (const addon of parentItemInCart.addons ?? []) {
        for (const choice of addon.choices ?? []) {
            out.push({
                group_id: addon.groupId,
                id: choice.id,
                name: choice.name,
            });
        }
    }
    for (const wrap of parentItemInCart.variantsV2 ?? []) {
        for (const vg of wrap.variantGroups ?? []) {
            for (const v of vg.variations ?? []) {
                out.push({
                    group_id: vg.groupId,
                    id: v.id,
                    name: v.name,
                });
            }
        }
    }
    return out;
};

export const getCrossSellParentImpressionSupplement = (
    context: CrossSellParentAnalyticsContext
): CrossSellParentImpressionSupplement => ({
    widget_type: context.widget_type,
    collection_name: context.collection_name,
    property_type: context.property_type,
    collection_id: context.collection_id,
    customizations: context.customizations,
    section: context.section,
    source_item_id: context.source_item_id,
    filter_enabled: context.filter_enabled,
    veg_filter_enabled: context.veg_filter_enabled,
});

export const buildParentCrossSellAnalyticsContext = (
    params: BuildParentCrossSellAnalyticsContextParams
): CrossSellParentAnalyticsContext => {
    const { parentDish, parentItemInCart, categoryMeta } = params;
    const quantity = parentItemInCart?.quantity ?? 1;
    const totalFinalPrice = parentItemInCart
        ? parentItemInCart.finalPrice * parentItemInCart.quantity
        : 0;
    const fallbackUnitPrice =
        parentItemInCart?.basePrice ?? parentDish?.price ?? parentDish?.finalPrice ?? 0;
    const parentType = categoryMeta?.parentType ?? CROSS_SELL_MENU_DEFAULT_PARENT_TYPE;
    const collectionLabel = categoryMeta?.collectionName ?? "";
    const collId = categoryMeta?.collectionId ?? "";
    const customizable = hasCustomizations(parentDish, parentItemInCart);
    const resolvedFinal =
        totalFinalPrice > 0
            ? Math.round(totalFinalPrice)
            : Math.round(Number(fallbackUnitPrice) * quantity);
    const sourceItemId = params.sourceItemId;
    const stackedPropertyType = collectionLabel
        ? `Native_QR_Menu_StackedWidget-->${collectionLabel}`
        : `Native_QR_Menu_StackedWidget-->${parentType}`;

    return {
        customizable: customizable ? CROSS_SELL_CUSTOMIZABLE_TRUE : CROSS_SELL_CUSTOMIZABLE_FALSE,
        quantity,
        final_price: resolvedFinal,
        widget_type: `Native_QR_Menu_StackedWidget-->${parentType}`,
        collection_name: collectionLabel,
        property_type: stackedPropertyType,
        collection_id: collId,
        customizations: buildCustomizationsPayload(parentItemInCart),
        section: CROSS_SELL_ANALYTICS_SECTION,
        source_item_id: sourceItemId,
        filter_enabled: params.filterEnabled,
        veg_filter_enabled: params.vegFilterEnabled,
    };
};
